/**
 * The booking lifecycle bar (Traci, 23 Sep 2026): completed stages fill in tan along a tan line; the current stage is
 * a thick tan ring with a pink halo (prototype v108); a partial stage (some talent done, not all) is a ring half filled; skipped and not-reached
 * stages stay grey. States come from src/lib/booking/lifecycleView.js.
 */
type Stage = { key: string; label: string; state: 'completed' | 'partial' | 'current' | 'skipped' | 'unreached'; done?: number; total?: number };

const TAN = '#b99a72';
const GREY = '#d9dee5';
const HALO = '#ffd7e9';

function Dot({ s }: { s: Stage }) {
  const base = 'h-[15px] w-[15px] rounded-full shrink-0 border-2';
  if (s.state === 'completed') return <span className={base} style={{ background: TAN, borderColor: TAN }} />;
  if (s.state === 'current') return <span className={base} style={{ background: '#fff', borderColor: TAN, borderWidth: 4, boxShadow: `0 0 0 3px ${HALO}` }} />;
  if (s.state === 'partial') return <span className={base} style={{ background: `linear-gradient(90deg, ${TAN} 50%, #fff 50%)`, borderColor: TAN, boxShadow: `0 0 0 3px ${HALO}` }} />;
  return <span className={base} style={{ background: '#fff', borderColor: '#aeb6c1' }} />;
}

const WHY: Record<Stage['state'], string> = { completed: 'Completed', current: 'Current stage', partial: 'Partly done', skipped: 'Skipped', unreached: 'Not reached' };

export default function LifecycleTrack({ stages, compact = false }: { stages: Stage[]; compact?: boolean }) {
  return (
    <ol className="flex w-full items-start overflow-x-auto pb-1" aria-label="Booking lifecycle">
      {stages.map((s, i) => {
        const next = stages[i + 1];
        const lineDone = s.state === 'completed' && next && (next.state === 'completed' || next.state === 'partial' || next.state === 'current');
        const title = `${s.label}: ${WHY[s.state]}${s.state === 'partial' && s.total ? ` (${s.done} of ${s.total} talent)` : ''}`;
        return (
          <li key={s.key} className="flex min-w-[82px] flex-1 flex-col items-center text-center" title={title} aria-label={title}>
            <div className="flex w-full items-center">
              <span className="h-0.5 flex-1" style={{ background: i === 0 ? 'transparent' : (s.state === 'completed' || s.state === 'partial' || s.state === 'current') && stages[i - 1]?.state === 'completed' ? TAN : GREY }} />
              <Dot s={s} />
              <span className="h-0.5 flex-1" style={{ background: !next ? 'transparent' : lineDone ? TAN : GREY }} />
            </div>
            {!compact && <span className={`mt-1 px-0.5 text-[10px] leading-tight ${s.state === 'current' || s.state === 'partial' ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{s.label}{s.state === 'partial' && s.total ? ` (${s.done}/${s.total})` : ''}</span>}
          </li>
        );
      })}
    </ol>
  );
}
